import React, { useRef } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import ConfirmModal from "../ConfirmModal";
import MoneyInput from "../MoneyInput";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { generateIdempotencyKey, setPendingIdempotencyKey } from "../../utils/idempotencyKey";
import { formatCentavos, pesosToCentavos } from "../../utils/whtMoney";

// Shared building blocks for the WHT return tabs (0619-E, 1601-EQ, 1601-C).
// DESIGN.md tokens only.

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Today's calendar date in Manila (UTC+8, no DST), 'YYYY-MM-DD'. */
export const manilaToday = (now = Date.now()) => new Date(now + 8 * 3600 * 1000).toISOString().slice(0, 10);
export const manilaDateOf = (instant) => manilaToday(new Date(instant).getTime());

/** 'YYYY-MM-DD' → 'MM/DD/YYYY' (the BIR forms' date format). */
export const bir_date = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : "—");

/** Centavos → "₱1,234.57"; a negative amount (over-remittance) → "(₱1,234.57)". */
export const money = (c) => {
  const n = Number(c) || 0;
  return n < 0 ? `(₱${formatCentavos(-n)})` : `₱${formatCentavos(n)}`;
};

export const rateLabel = (bp) => `${(Number(bp) / 100).toFixed(2)}%`;

/** Status chips for a period from a year summary (a month or a quarter). */
export function statusChips(p, today) {
  const chips = [];
  if (p.status === "filed") chips.push(p.isAmended ? "Amended" : "Filed");
  else if (p.status === "draft") chips.push(p.isAmended ? "Amendment draft" : "Draft");
  else chips.push("Not started");
  // Overdue: nothing filed yet (an amendment draft means the original was filed).
  const everFiled = p.status === "filed" || p.isAmended;
  if (!everFiled && p.adjustedDueDate && today > p.adjustedDueDate) chips.push("Overdue");
  return chips;
}

const CHIP_STYLE = {
  "Not started": "bg-ink-canvas text-ink-body",
  Draft: "bg-primary-50 text-primary-700",
  "Amendment draft": "bg-primary-50 text-primary-700",
  Filed: "bg-success-50 text-success-700",
  Amended: "bg-success-50 text-success-700",
  Overdue: "bg-danger-50 text-danger-700",
};

export const Chip = ({ label }) => (
  <span className={`text-xs px-2 py-0.5 rounded-md ${CHIP_STYLE[label] || CHIP_STYLE["Not started"]}`}>{label}</span>
);

export function displayValue(kind, v) {
  if (kind === "money") return money(v);
  if (kind === "bool") return v ? "Yes" : "No";
  if (kind === "date") return bir_date(v);
  if (kind === "category") return v === "government" ? "Government" : "Private";
  return v === undefined || v === null || v === "" ? "—" : String(v);
}

export const ItemNo = ({ no }) => (
  <span className="text-xs font-medium text-primary-700 bg-primary-50 rounded-md px-2 py-0.5 text-center tabular-nums">{no}</span>
);

export const ItemRow = ({ no, label, children, note }) => (
  <div className="py-2 border-b border-ink-line last:border-b-0" data-item={no}>
    <div className="grid grid-cols-[3.5rem_1fr_auto] items-start gap-3">
      <ItemNo no={no} />
      <span className="text-sm text-ink-body">{label}</span>
      <span className="text-sm text-ink text-right tabular-nums" data-testid={`item-${no}`}>{children}</span>
    </div>
    {note}
  </div>
);

export const Part = ({ title, children }) => (
  <section className="border border-ink-line rounded-lg bg-white p-4 mb-4" aria-label={title}>
    <h4 className="text-sm font-medium text-ink mb-2">{title}</h4>
    {children}
  </section>
);

export const inputClass = "border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";

export const TaxpayerSizeBanner = ({ clientId }) => (
  <div role="note" aria-label="Taxpayer size needed" className="mb-4 border border-warning-200 bg-warning-50 rounded-lg p-3 flex items-start gap-2">
    <AlertTriangle className="w-4 h-4 mt-0.5 text-warning-700" />
    <p className="text-sm text-warning-700">
      The client's taxpayer size is not set, so penalties use the regular rates (25% / 12%).{" "}
      <a href={`/client/accounting-clients/${clientId}/edit`} className="font-medium underline">
        Set the taxpayer size in the client profile
      </a>.
    </p>
  </div>
);

export const FiledBadge = ({ filedAt }) => (
  <span data-testid="filed-badge" className="text-xs px-2 py-0.5 rounded-md bg-success-50 text-success-700 flex items-center gap-1">
    <CheckCircle2 className="w-3 h-3" /> Filed on {bir_date(manilaDateOf(filedAt))}
  </span>
);

export const AmberNote = ({ children }) => (
  <p className="mt-1 ml-[4.25rem] text-xs text-warning-700 flex items-start gap-1">
    <AlertTriangle className="w-3 h-3 mt-0.5" /> <span>{children}</span>
  </p>
);

// ── Details of payment ───────────────────────────────────────────────────

/** rows: [[itemNo, type, label], …] in form order. */
export const emptyPayments = (rows) =>
  Object.fromEntries(rows.map(([, type]) => [type, { bank: "", number: "", date: "", amount: 0, particulars: "" }]));

export function paymentsFromView(rows, payment) {
  const out = emptyPayments(rows);
  for (const p of payment || []) {
    out[p.type] = { bank: p.bank || "", number: p.number || "", date: p.date || "", amount: (p.amountCentavos || 0) / 100, particulars: p.particulars || "" };
  }
  return out;
}

export const paymentPayload = (rows, payments) => rows
  .map(([, type]) => ({ type, ...payments[type] }))
  .filter((p) => p.bank || p.number || p.date || Number(p.amount) || p.particulars)
  .map((p) => ({
    type: p.type, bank: p.bank, number: p.number, date: p.date || null,
    amountCentavos: pesosToCentavos(p.amount) || 0, particulars: p.particulars,
  }));

/** Server field errors → { 'payment.<type>' | <path>: message }. */
export function mapFieldErrors(e, payload) {
  const fields = e?.lineErrors?.[0]?.fields || {};
  const mapped = {};
  for (const [path, msg] of Object.entries(fields)) {
    const m = /^payment\.(\d+)\./.exec(path);
    const key = m ? `payment.${payload?.payment?.[Number(m[1])]?.type}` : path;
    mapped[key] = mapped[key] ? `${mapped[key]}; ${msg}` : msg;
  }
  return mapped;
}

export const PaymentDetails = ({ title, rows, payments, readOnly, fieldErrors = {}, onChange }) => (
  <Part title={title}>
    <div className="grid grid-cols-[3.5rem_1fr_1fr_1fr_1fr_1fr] gap-2 text-xs font-medium text-ink-body uppercase tracking-wider pb-1">
      <span />
      <span>Particulars</span>
      <span>Drawee bank / agency</span>
      <span>Number</span>
      <span>Date</span>
      <span className="text-right">Amount</span>
    </div>
    {rows.map(([no, type, label]) => {
      const p = payments[type];
      const err = fieldErrors[`payment.${type}`];
      const set = (patch) => onChange(type, patch);
      return (
        <div key={no} data-item={no} className="py-2 border-b border-ink-line last:border-b-0">
          <div className="grid grid-cols-[3.5rem_1fr_1fr_1fr_1fr_1fr] gap-2 items-center">
            <ItemNo no={no} />
            {readOnly ? (
              <>
                <span className="text-sm text-ink-body">{type === "other" && p.particulars ? `${label}: ${p.particulars}` : label}</span>
                <span className="text-sm text-ink">{p.bank || "—"}</span>
                <span className="text-sm text-ink">{p.number || "—"}</span>
                <span className="text-sm text-ink">{bir_date(p.date)}</span>
                <span className="text-sm text-ink text-right tabular-nums" data-testid={`item-${no}`}>{money(pesosToCentavos(p.amount))}</span>
              </>
            ) : (
              <>
                {type === "other" ? (
                  <input aria-label={`Item ${no} particulars`} value={p.particulars} onChange={(e) => set({ particulars: e.target.value })} className={inputClass} placeholder={label} />
                ) : (
                  <span className="text-sm text-ink-body">{label}</span>
                )}
                <input aria-label={`Item ${no} bank`} value={p.bank} onChange={(e) => set({ bank: e.target.value })} className={inputClass} />
                <input aria-label={`Item ${no} number`} value={p.number} onChange={(e) => set({ number: e.target.value })} className={inputClass} />
                <input aria-label={`Item ${no} date`} type="date" value={p.date} onChange={(e) => set({ date: e.target.value })} className={inputClass} />
                <MoneyInput id={`item-${no}-amount`} value={p.amount} onValueChange={(v) => set({ amount: v })} className={`${inputClass} text-right`} />
              </>
            )}
          </div>
          {err && <p role="alert" className="text-xs text-danger-500 mt-1">{err}</p>}
        </div>
      );
    })}
  </Part>
);

// ── Mark as filed ────────────────────────────────────────────────────────

export const FileConfirmModal = ({ isOpen, title, filedDate, onDateChange, loading, onConfirm, onCancel, children }) => (
  <ConfirmModal
    isOpen={isOpen}
    title={title}
    message={
      <span className="block">
        <label className="block text-sm text-ink-body mb-2">
          Filing date
          <input
            type="date"
            aria-label="Filing date"
            value={filedDate}
            max={manilaToday()}
            onChange={(e) => onDateChange(e.target.value)}
            className={`${inputClass} block mt-1`}
          />
        </label>
        {children}
        A filed return is read-only. To change it later, amend it.
      </span>
    }
    confirmLabel="Mark as filed"
    variant="primary"
    loading={loading}
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
);

/**
 * Save draft / Mark as filed / Amend for a WHT return, each through
 * useGuardedMutation.
 *
 * "Mark as filed" saves pending draft edits first (PUT), then files (POST).
 * useGuardedMutation's Idempotency-Key goes to the FIRST request only (the
 * PUT), so the file POST carries its own key — rotated only after a
 * successful filing, so a retry after a dropped response replays.
 *
 * @param {object} p
 * @param {string}   p.label         e.g. '0619-E · Jan 2026' (for messages)
 * @param {Function} p.saveRequest   () => Promise<response>
 * @param {Function} p.fileRequest   (filedAtIso) => Promise<response>
 * @param {Function} p.amendRequest  () => Promise<response>
 * @param {Function} p.onChanged     async (response.data) => void (reload)
 * @param {Function} [p.onSaveError] (error) => void
 * @param {Function} [p.onFileError] (error) => boolean — true when handled (no toast)
 * @param {Function} [p.onFiled]     () => void
 */
export function useReturnActions({ label, saveRequest, fileRequest, amendRequest, onChanged, onSaveError, onFileError, onFiled }) {
  const saveDraft = async () => {
    try {
      const res = await saveRequest();
      notify.success("Saved", `${label} draft saved.`);
      await onChanged(res.data);
      return true;
    } catch (e) {
      onSaveError?.(e);
      notify.error("Couldn't save", e.message);
      return false;
    }
  };
  const { mutate: runSave, isPending: saving } = useGuardedMutation(saveDraft);

  const fileKeyRef = useRef(generateIdempotencyKey());
  const fileReturn = async (filedDate) => {
    if (!(await saveDraft())) return;
    try {
      setPendingIdempotencyKey(fileKeyRef.current);
      // Noon in Manila: only the Manila calendar DATE matters for penalties.
      const res = await fileRequest(`${filedDate}T12:00:00+08:00`);
      fileKeyRef.current = generateIdempotencyKey();
      onFiled?.();
      notify.success("Filed", `${label} marked as filed on ${bir_date(filedDate)}.`);
      await onChanged(res.data);
    } catch (e) {
      if (!onFileError?.(e)) notify.error("Couldn't file", e.message);
    }
  };
  const { mutate: runFile, isPending: filing } = useGuardedMutation(fileReturn);

  const amendReturn = async () => {
    try {
      const res = await amendRequest();
      notify.success("Amendment started", "A new draft was created from the filed return.");
      await onChanged(res.data);
    } catch (e) {
      notify.error("Couldn't amend", e.message);
    }
  };
  const { mutate: runAmend, isPending: amending } = useGuardedMutation(amendReturn);

  return { runSave, saving, runFile, filing, runAmend, amending };
}
